import { useRef, useEffect, useCallback } from "react";
import { useWebsocketStore } from "@/features/tutor/store/useWebsocketStore";
import { useSessionStore } from "@/features/tutor/store/useSessionStore";
import { useTurnStore } from "@/features/tutor/store/useTurnStore";
import { useSlideStore } from "@/features/tutor/store/useSlideStore";
import { useRuntimeContext } from "@/features/tutor/context/RuntimeContext";
import useLogger from "./useLogger";
import { useLoadStore } from "@/features/tutor/store/useLoadStore";
import { useConnectionStore } from "@/features/tutor/store/useConnectionStore";
import { v4 as uuidv4 } from "uuid";
import { getRandomInactivityKey } from "@/features/tutor/constants/presetMessages";
import { compress, decompress, CompressionMetrics } from "@/utils/compression";
import { emitDevEvent } from "@/devHooks/devHookConnector";
import {
  addResumeDebugEvent,
  useResumeDebugStore,
} from "@/store/useResumeDebugStore";
import {
  getLessonStartupPerfNow,
  markLessonStartupWsOpen,
  recordLessonStartupPerf,
} from "@/features/tutor/utils/lessonStartupPerf";
import {
  useNotepadStore,
  selectIsNotepadOpen,
} from "@/features/notepad/store/useNotepadStore";
import { useAudioStore } from "@/features/tutor/store/useAudioStore";
import {
  dispatchTutorTurn,
  settleTutorTurn,
} from "@/features/tutor/utils/tutorTurnLifecycle";
import { isSlideChangePending } from "@/features/tutor/utils/tutorSpeechSnapshot";
import { useAuthStore } from "@/store/useAuthStore";
import { TOKEN_KEY } from "@/constants/auth";
import { buildLessonWebSocketUrl } from "./buildLessonWebSocketUrl";
import { isAuthFailureWebSocketCloseCode } from "./isAuthFailureWebSocketCloseCode.js";
import { debugLog, debugWarn } from "@/utils/debugLog";
import {
  buildAudioChunk,
  buildDataChunk,
  buildDoneChunk,
  buildTextChunk,
  buildTurnChangeChunk,
  getWebSocketRetryDelayMs,
  parseDiscussContextFailed,
  parseTextDebugOutput,
  parseTurnStart,
  parseTutorActionDebugPayload,
  parseUploadedImagesStatus,
  shouldIgnoreExtraChunkAfterDone,
  shouldIgnoreInboundChunkForTurn,
  shouldRecordInboundResumeDebug,
  shouldRetryWebSocketClose,
  shouldSuppressTutorInboundChunk,
} from "@/features/tutor/vo-demo/ws/protocol";

export const useWebsocketClient = () => {
  const setTurnId = useTurnStore((state) => state.setTurnId);
  const sessionId = useSessionStore((state) => state.sessionId);
  debugLog("sessionId for ws connection", sessionId);
  const { addLog } = useLogger();

  const wsRef = useRef(null);
  const socketSessionIdRef = useRef(null);
  const reconnectFnRef = useRef(null);
  const outboundMetricsRef = useRef(null);
  const inboundMetricsRef = useRef(null);
  const micSendLockedRef = useRef(false);
  const wsConnectStartedAtRef = useRef(null);
  const firstAcceptedAudioChunkRef = useRef(false);
  const hasConnectedBeforeRef = useRef(false);
  const pendingNotepadLifecycleRef = useRef(null);

  const { emit, subscribe, doneChunkRecievedRef, getDelta, resetClock } =
    useRuntimeContext();

  const latestRuntimeRef = useRef({
    addLog,
    doneChunkRecievedRef,
    emit,
    resetClock,
  });
  latestRuntimeRef.current = {
    addLog,
    doneChunkRecievedRef,
    emit,
    resetClock,
  };

  const sendCompressed = useCallback((data) => {
    if (
      wsRef.current?.readyState !== WebSocket.OPEN ||
      socketSessionIdRef.current !== useSessionStore.getState().sessionId
    )
      return false;
    const jsonStr = typeof data === "string" ? data : JSON.stringify(data);
    const { data: compressed, stats } = compress(jsonStr);
    outboundMetricsRef.current?.record(stats, "out");
    try {
      wsRef.current.send(compressed);
      if (__DEV_OVERLAY__) {
        const chunk = typeof data === "object" ? data : null;
        emitDevEvent("ws.outbound", {
          chunkType: chunk?.chunk_type ?? null,
          turnId: chunk?.turn_id ?? null,
        });
      }
      return true;
    } catch (error) {
      debugWarn("[WS] Send failed", error);
      return false;
    }
  }, []);

  const flushNotepadLifecycle = useCallback(() => {
    const pending = pendingNotepadLifecycleRef.current;
    if (!pending) return;
    const session = useSessionStore.getState().sessionId;
    const slide = useSlideStore.getState();
    if (
      pending.lessonId !== slide.currentLessonId ||
      pending.slideIndex !== slide.currentSlideIndex ||
      (pending.sessionId && pending.sessionId !== session)
    ) {
      pendingNotepadLifecycleRef.current = null;
      return;
    }
    if (!session) return;
    const send = (chunkType) =>
      sendCompressed(
        buildDataChunk({
          chunkType,
          chunkData:
            chunkType === "NOTEPAD_CLOSED"
              ? JSON.stringify({ ingress_type: pending.ingressType })
              : "",
          timestamp: getDelta(),
        }),
      );
    if (pending.wasOpened) {
      if (!send("NOTEPAD_OPENED")) return;
      pending.wasOpened = false;
    }
    if (pending.finalType !== "NOTEPAD_OPENED" && !send(pending.finalType))
      return;
    pendingNotepadLifecycleRef.current = null;
  }, [getDelta, sendCompressed]);

  const recordNotepadLifecycle = useCallback(
    (chunkType, detail) => {
      const { currentLessonId: lessonId, currentSlideIndex: slideIndex } =
        useSlideStore.getState();
      const sessionId = useSessionStore.getState().sessionId;
      const previous = pendingNotepadLifecycleRef.current;
      pendingNotepadLifecycleRef.current = {
        sessionId,
        lessonId,
        slideIndex,
        wasOpened:
          chunkType === "NOTEPAD_OPENED" ||
          !!(
            previous?.wasOpened &&
            previous.lessonId === lessonId &&
            previous.slideIndex === slideIndex &&
            (!previous.sessionId || previous.sessionId === sessionId)
          ),
        finalType: chunkType,
        ingressType: detail?.ingress_type || "ai",
      };
      flushNotepadLifecycle();
    },
    [flushNotepadLifecycle],
  );

  window.wsRef = wsRef;
  window.inputWsRef = wsRef;
  window.outputWsRef = wsRef;

  useEffect(() => {
    const unsubTurnChange = subscribe("TURN_CHANGE", (opts) => {
      if (useAudioStore.getState().isNudgePending) {
        useAudioStore.getState().setIsNudgePending(false);
      }
      const turnStore = useTurnStore.getState();
      if (turnStore.isResumeTurnLocked()) {
        addResumeDebugEvent("ws:emit:TURN_CHANGE:blocked_resume_lock", {
          lock_until_ms: turnStore.getResumeTurnLockUntilMs(),
        });
        addLog("[WS] Skipped TURN_CHANGE during resume lock window");
        return;
      }
      const newTurnId = uuidv4();
      addLog(`TURN_CHANGE ${newTurnId}`);
      addResumeDebugEvent("ws:emit:TURN_CHANGE", { turn_id: newTurnId });

      // Interrupt timestamp must be in audio-elapsed milliseconds so the
      // backend's _handle_partial_response can match it against token
      // start_ts values. Wall-clock-since-TURN_CHANGE (getDelta) bakes in
      // ~500-1500ms of pre-audio LLM/STT/TTS latency and would cause
      // truncation to keep too much of the assistant message.
      // Prefer the press-time snapshot; reading the live player here is
      // order-dependent — see utils/tutorSpeechSnapshot.js for why. Emitters
      // that carry no snapshot (slide change, inactivity, canvas) keep the
      // live read: "the student barged in" does not apply to them anyway.
      const audioStartMs =
        opts?.audioStartMs ?? window.__tutorAudioStartWallClockMs;
      // A pending next-slide countdown is an interrupt too: audio has ended, but
      // the slide has not changed and this turn cancels it.
      const wasSlideChangePending =
        opts?.wasSlideChangePending ?? isSlideChangePending();
      const isInterrupt =
        (opts?.wasTutorSpeaking ?? window.pcmPlayer?.isPlaying() ?? false) ||
        wasSlideChangePending;
      const interruptTimestampMs =
        isInterrupt && audioStartMs ? Date.now() - audioStartMs : getDelta();
      debugLog("timestamp", interruptTimestampMs);

      // A mute toggled mid-response lands here, and it has to ride this chunk
      // rather than the separate TUTOR_AUDIO one: the submission chunk that
      // starts the turn is sent immediately after this, so a separately queued
      // preference would race the backend opening its TTS service.
      const tutorAudioOn = dispatchTutorTurn();
      // Not an interrupt: the tutor may have answered since its NextSlide, so
      // the backend looks for the tag itself rather than at the last message.
      const hasFailedSlideChange =
        useSlideStore.getState().hasFailedAiSlideChange;

      const sent = sendCompressed(
        buildTurnChangeChunk({
          turnId: newTurnId,
          timestamp: interruptTimestampMs,
          isInterrupt,
          captureProfile:
            opts?.source === "mic"
              ? useAudioStore.getState().micCaptureProfile
              : null,
          tutorAudioOn,
          hasFailedSlideChange,
        }),
      );

      if (sent) {
        micSendLockedRef.current = false;
        // Handed to an OPEN socket, not acknowledged: a half-open one loses it.
        if (hasFailedSlideChange) {
          useSlideStore.setState({ hasFailedAiSlideChange: false });
        }
        setTurnId(newTurnId);
        if (opts?.source !== "inactivity") {
          emit("USER_ACTIVITY");
        }
        const canvasWs = useNotepadStore.getState().notepadWs;
        if (canvasWs?.readyState === WebSocket.OPEN) {
          const canvasTurnChange = buildTurnChangeChunk({
            turnId: newTurnId,
            timestamp: interruptTimestampMs,
            isInterrupt,
            tutorAudioOn,
          });
          const { data: compressed } = compress(
            JSON.stringify(canvasTurnChange),
          );
          canvasWs.send(compressed);
        } else if (selectIsNotepadOpen(useNotepadStore.getState())) {
          addLog("[Canvas WS] Skipped TURN_CHANGE - WebSocket not open");
        }
        resetClock();
      } else {
        debugWarn("[WS] Cannot send TURN_CHANGE - WebSocket not open");
        addLog(`[WS] Skipped TURN_CHANGE - WebSocket not open`);
        micSendLockedRef.current = true;
        // A missed nudge is not the student's request; their next turn retries.
        if (opts?.source !== "inactivity") turnStore.setTurnChangeFailed(true);
        // Nothing reached the backend, so no turn is in flight: leaving the
        // dispatch flag set would defer every later toggle forever.
        settleTutorTurn();
        useConnectionStore.getState().markDisconnected();
      }
    });

    const unsubUserAudio = subscribe("USER_AUDIO_CHUNK", (chunk_data) => {
      if (
        selectIsNotepadOpen(useNotepadStore.getState()) ||
        micSendLockedRef.current
      ) {
        return;
      }
      if (
        !sendCompressed(
          buildAudioChunk({
            chunkData: chunk_data,
            timestamp: getDelta(),
          }),
        )
      ) {
        debugWarn("[WS] Cannot send AUDIO chunk - WebSocket not open");
      }
    });

    const unsubUserAudioDone = subscribe("USER_AUDIO_DONE", () => {
      if (selectIsNotepadOpen(useNotepadStore.getState())) {
        addLog("[WS] DONE skipped — notepad open");
        return;
      }
      if (micSendLockedRef.current) {
        addLog("[WS] DONE skipped — mic locked (WS disconnect)");
        return;
      }
      const wsReadyState = wsRef.current?.readyState ?? -1;
      if (
        !sendCompressed(
          buildDoneChunk({
            turnId: useTurnStore.getState().getTurnId(),
            timestamp: getDelta(),
          }),
        )
      ) {
        debugWarn("[WS] Cannot send DONE - WebSocket not open");
        addLog(
          `[WS] DONE skipped — WebSocket not open (readyState=${wsReadyState})`,
        );
      }
    });

    const unsubUserInactivity = subscribe("USER_INACTIVITY", () => {
      // Guard: don't nudge if any AI (tutor or canvas) is currently speaking
      if (useAudioStore.getState().isAITalking) {
        addLog("[WS] Skipped INACTIVITY - AI is speaking");
        return;
      }

      emit("TURN_CHANGE", { source: "inactivity" });

      // Set pending flag AFTER TURN_CHANGE so the TURN_CHANGE handler doesn't reset it
      const notepadState = useNotepadStore.getState();
      if (
        selectIsNotepadOpen(notepadState) &&
        notepadState.ingressType === "ai"
      ) {
        useAudioStore.getState().setIsNudgePending(true);
      }
      const key = getRandomInactivityKey();
      if (
        !sendCompressed(
          buildDataChunk({
            chunkType: "INACTIVITY",
            chunkData: key,
            timestamp: getDelta(),
          }),
        )
      ) {
        debugWarn("[WS] Cannot send INACTIVITY - WebSocket not open");
        addLog(`[WS] Skipped INACTIVITY - WebSocket not open`);
        useAudioStore.getState().setIsNudgePending(false); // rollback if send failed
      }
    });

    const unsubInputMode = subscribe("INPUT_MODE_CHANGED", (mode) => {
      if (
        !sendCompressed(
          buildDataChunk({
            chunkType: "INPUT_MODE",
            chunkData: mode,
            timestamp: getDelta(),
          }),
        )
      ) {
        addLog("[WS] Skipped INPUT_MODE - WebSocket not open");
      }
    });

    const unsubTutorAudio = subscribe("TUTOR_AUDIO_CHANGED", (audioOn) => {
      if (
        !sendCompressed(
          buildDataChunk({
            chunkType: "TUTOR_AUDIO",
            chunkData: audioOn ? "true" : "false",
            timestamp: getDelta(),
          }),
        )
      ) {
        addLog("[WS] Skipped TUTOR_AUDIO - WebSocket not open");
      }
    });

    const unsubUserText = subscribe("USER_TEXT_CHUNK", (payload) => {
      if (micSendLockedRef.current) return;
      const { text, model, source, inputMode, tutorAudioOn } =
        typeof payload === "string" ? { text: payload } : (payload ?? {});
      if (
        !sendCompressed(
          buildTextChunk({
            userMessage: text,
            model,
            source,
            inputMode,
            tutorAudioOn,
            timestamp: getDelta(),
          }),
        )
      ) {
        debugWarn("[WS] Cannot send TEXT - WebSocket not open");
        addLog(`[WS] Skipped TEXT - WebSocket not open`);
      }
    });

    const unsubDiscussContext = subscribe("DISCUSS_CONTEXT", (jsonPayload) => {
      if (
        !sendCompressed(
          buildDataChunk({
            chunkType: "DISCUSS_CONTEXT",
            chunkData: jsonPayload,
            timestamp: getDelta(),
          }),
        )
      ) {
        debugWarn("[WS] Cannot send DISCUSS_CONTEXT - WebSocket not open");
        addLog(`[WS] Skipped DISCUSS_CONTEXT - WebSocket not open`);
      } else {
        emit("USER_ACTIVITY");
      }
    });

    const unsubReconnectRecovery = subscribe("RECONNECT_RECOVERY", () => {
      if (
        !sendCompressed(
          buildDataChunk({
            chunkType: "RECONNECT_RECOVERY",
            timestamp: getDelta(),
          }),
        )
      ) {
        debugWarn("[WS] Cannot send RECONNECT_RECOVERY - WebSocket not open");
        addLog(`[WS] Skipped RECONNECT_RECOVERY - WebSocket not open`);
      } else {
        addLog("[WS] RECONNECT_RECOVERY sent");
      }
    });

    const unsubNotepadClosed = subscribe("NOTEPAD_CLOSED", (detail) => {
      recordNotepadLifecycle("NOTEPAD_CLOSED", detail);
    });
    const unsubNotepadOpened = subscribe("NOTEPAD_OPENED", () => {
      recordNotepadLifecycle("NOTEPAD_OPENED");
    });
    const unsubNotepadNotOpened = subscribe("NOTEPAD_NOT_OPENED", () => {
      recordNotepadLifecycle("NOTEPAD_NOT_OPENED");
    });

    const sendActivityChunk = () => {
      sendCompressed(
        buildDataChunk({
          chunkType: "USER_ACTIVITY",
          timestamp: getDelta(),
        }),
      );
    };

    const unsubUserActivity = subscribe("USER_ACTIVITY", sendActivityChunk);

    const sendNotepadActivityChunk = () => {
      // Mirror of sendActivityChunk for the notepad surface. Always routes to
      // the tutor BE WS (not the canvas gateway WS). Backend's
      // `handle_notepad_activity` is a no-op when notepad isn't marked open,
      // so a stray ping is harmless.
      sendCompressed(
        buildDataChunk({
          chunkType: "NOTEPAD_ACTIVITY",
          timestamp: getDelta(),
        }),
      );
    };

    const unsubNotepadActivity = subscribe(
      "NOTEPAD_ACTIVITY",
      sendNotepadActivityChunk,
    );

    const handleActivityEvent = () => sendActivityChunk();
    window.addEventListener("user:activity", handleActivityEvent);

    return () => {
      unsubTurnChange();
      unsubUserAudio();
      unsubUserAudioDone();
      unsubUserText();
      unsubUserInactivity();
      unsubInputMode();
      unsubTutorAudio();
      unsubDiscussContext();
      unsubReconnectRecovery();
      unsubNotepadClosed();
      unsubNotepadOpened();
      unsubNotepadNotOpened();
      unsubUserActivity();
      unsubNotepadActivity();
      window.removeEventListener("user:activity", handleActivityEvent);
    };
  }, [
    addLog,
    emit,
    getDelta,
    resetClock,
    setTurnId,
    subscribe,
    sendCompressed,
    recordNotepadLifecycle,
  ]);

  useEffect(() => {
    if (!sessionId) return;

    let active = true;
    let activeSocket = null;
    firstAcceptedAudioChunkRef.current = false;
    hasConnectedBeforeRef.current = false;
    outboundMetricsRef.current = new CompressionMetrics(sessionId);
    inboundMetricsRef.current = new CompressionMetrics(sessionId);

    const baseUrl =
      import.meta.env.VITE_WS_BASE_URL || "ws://localhost:8000/api/v1/ws";

    const connectWithRetry = (
      wsRefParam,
      onOpen,
      onClose,
      onMessage,
      onError,
    ) => {
      let attempts = 0;
      const maxAttempts = 5;
      let retryTimeoutId = null;

      const connect = () => {
        if (!active) {
          debugLog("[WS] Skipping connection - stale connection");
          return;
        }

        if (retryTimeoutId) {
          clearTimeout(retryTimeoutId);
          retryTimeoutId = null;
        }
        if (
          activeSocket?.readyState === WebSocket.CONNECTING ||
          activeSocket?.readyState === WebSocket.OPEN
        ) {
          return;
        }

        const token = localStorage.getItem(TOKEN_KEY);
        const demoClientId = sessionStorage.getItem("demoClientId");

        const fullUrl = buildLessonWebSocketUrl({
          baseUrl,
          sessionId,
          token,
          demoClientId,
        });
        const connectStartedAt = getLessonStartupPerfNow();
        wsConnectStartedAtRef.current = connectStartedAt;
        recordLessonStartupPerf("ws_connect_begin", {
          session_id: sessionId,
          lesson_id: useSlideStore.getState().currentLessonId,
          attempt: attempts + 1,
        });

        const ws = new WebSocket(fullUrl);
        activeSocket = ws;
        wsRefParam.current = ws;
        socketSessionIdRef.current = sessionId;
        ws.binaryType = "arraybuffer";

        ws.onopen = () => {
          if (!active) {
            ws.close(1000, "Stale connection");
            return;
          }
          attempts = 0;
          markLessonStartupWsOpen(sessionId);
          recordLessonStartupPerf(
            "ws_open",
            {
              session_id: sessionId,
              lesson_id: useSlideStore.getState().currentLessonId,
            },
            connectStartedAt,
          );
          markLessonStartupWsOpen(sessionId);
          if (__DEV_OVERLAY__) emitDevEvent("ws.lifecycle", { phase: "open" });
          onOpen();
        };

        ws.onclose = (event) => {
          const { addLog } = latestRuntimeRef.current;

          // Clear any pending retry timeout
          if (retryTimeoutId) {
            clearTimeout(retryTimeoutId);
            retryTimeoutId = null;
          }

          if (!active) {
            return;
          }
          // After the active check, like onopen: a socket this effect tore
          // down is not a failure.
          if (__DEV_OVERLAY__) {
            emitDevEvent("ws.lifecycle", {
              phase: "close",
              code: event.code,
              reason: event.reason,
            });
          }

          onClose(event);

          // Don't retry on certain close codes that indicate permanent closure
          const shouldRetry = shouldRetryWebSocketClose(event.code);

          if (shouldRetry && navigator.onLine === false) {
            debugLog(
              `[WS] Closed with code ${event.code} while offline, waiting for the browser to come back online`,
            );
            return;
          }

          if (shouldRetry && attempts < maxAttempts && active) {
            const retryDelay = getWebSocketRetryDelayMs(attempts);
            debugLog(
              `[WS] Closed with code ${event.code}, will retry in ${retryDelay}ms (attempt ${attempts + 1}/${maxAttempts})`,
            );
            retryTimeoutId = setTimeout(() => {
              if (active) {
                connect();
              }
            }, retryDelay);
            attempts++;
          } else if (!shouldRetry) {
            debugWarn(
              `[WS] Closed with code ${event.code} - permanent closure, not retrying`,
            );
            addLog(
              `[WS] Permanent closure (code ${event.code}) - session ended`,
            );
            if (isAuthFailureWebSocketCloseCode(event.code)) {
              void useAuthStore
                .getState()
                .clearAuth({ skipServerSignOut: true })
                .finally(() => {
                  window.location.href = "/login";
                });
              return;
            }
            if (sessionId && active) {
              useSessionStore.getState().setSessionId(null);
            }
          } else {
            console.error(
              `[WS] Max retry attempts (${maxAttempts}) reached, giving up`,
            );
            if (sessionId && active) {
              useSessionStore.getState().setSessionId(null);
            }
          }
        };

        ws.onerror = (err) => {
          const { addLog } = latestRuntimeRef.current;

          if (!active) {
            return;
          }
          if (__DEV_OVERLAY__) emitDevEvent("ws.lifecycle", { phase: "error" });

          const errorInfo = {
            type: err.type,
            timestamp: err.timeStamp,
            target: err.target,
            readyState: err.target?.readyState,
            url: err.target?.url,
          };

          console.error("[WS Error]", errorInfo);
          addLog(`[WS Error] ${JSON.stringify(errorInfo)}`);

          // Log specific WebSocket state information
          if (err.target?.readyState === WebSocket.CONNECTING) {
            console.error("WebSocket failed to connect");
            addLog("[WS Error] Failed to connect");
          } else if (err.target?.readyState === WebSocket.OPEN) {
            console.error("WebSocket connection error while open");
            addLog("[WS Error] Connection error while open");
          } else if (err.target?.readyState === WebSocket.CLOSING) {
            console.error("WebSocket error while closing");
            addLog("[WS Error] Error while closing");
          } else if (err.target?.readyState === WebSocket.CLOSED) {
            console.error("WebSocket error while closed");
            addLog("[WS Error] Error while closed");
          }

          onError(err);
          // Note: Retry logic is handled by onclose, which fires after onerror
        };

        ws.onmessage = onMessage;
      };

      connect();

      // Return connect function for manual reconnection and cleanup function
      const cleanup = () => {
        if (retryTimeoutId) {
          clearTimeout(retryTimeoutId);
          retryTimeoutId = null;
        }
      };

      // Store cleanup function on the connect function for later use
      connect.cleanup = cleanup;

      return connect;
    };

    const handleMessage = (event) => {
      const { addLog, doneChunkRecievedRef, emit, resetClock } =
        latestRuntimeRef.current;

      if (!active) return;

      let chunk;
      try {
        const compressedData = new Uint8Array(event.data);
        const { data: jsonStr, stats } = decompress(compressedData);
        inboundMetricsRef.current?.record(stats, "in");
        chunk = JSON.parse(jsonStr);
      } catch (err) {
        console.error("Error decompressing/parsing WS chunk:", err);
        return;
      }

      const { chunk_type, chunk_data, turn_id } = chunk;
      debugLog("ws received chunk_type:", chunk_type, "turn_id:", turn_id);
      if (__DEV_OVERLAY__) {
        emitDevEvent("ws.inbound", {
          chunkType: chunk_type,
          turnId: turn_id ?? null,
        });
      }

      if (shouldRecordInboundResumeDebug(chunk_type)) {
        addResumeDebugEvent("ws:inbound:chunk", {
          chunk_type,
          turn_id,
        });
      }

      if (chunk_type === "RESET") return;

      const currentTurnId = useTurnStore.getState().getTurnId();
      if (shouldIgnoreInboundChunkForTurn(turn_id, currentTurnId)) {
        addResumeDebugEvent("ws:inbound:ignored_turn_mismatch", {
          chunk_type,
          chunk_turn_id: turn_id,
          current_turn_id: currentTurnId,
        });
        if (chunk_type === "TUTOR_ACTION") {
          addLog(
            `Ignoring Tutor Action chunk with turn_id ${turn_id} - not the current turn ${currentTurnId}`,
          );
          return;
        }
        addLog(
          `Ignoring chunk with turn_id ${turn_id} - not the current turn ${currentTurnId}`,
        );
        return;
      }

      const shouldSuppressTutorChunk = shouldSuppressTutorInboundChunk({
        chunkType: chunk_type,
        isNotepadOpen: selectIsNotepadOpen(useNotepadStore.getState()),
        isNudgePending: useAudioStore.getState().isNudgePending,
      });
      if (shouldSuppressTutorChunk) {
        addLog(`[WS] Suppressing tutor ${chunk_type} while canvas is open`);
        return;
      }

      if (chunk_type === "TUTOR_ACTION") {
        try {
          addResumeDebugEvent(
            "ws:inbound:TUTOR_ACTION",
            parseTutorActionDebugPayload(chunk_data),
          );
        } catch {
          addResumeDebugEvent("ws:inbound:TUTOR_ACTION_parse_error");
        }
        addLog(`Handling Tutor Action chunk with turn_id ${turn_id}`);
        useSlideStore.getState().incrementSlideActionCount();
        emit("TUTOR_ACTION", { chunk_data, turn_id });
        addLog(`EVENT: TUTOR_ACTION`);
        return;
      }

      if (chunk_type === "UPLOADED_IMAGES_STATUS") {
        try {
          const { failedImages } = parseUploadedImagesStatus(chunk_data);
          emit("UPLOADED_IMAGES_STATUS", { failedImages, turnId: turn_id });
          addLog(`EVENT: UPLOADED_IMAGES_STATUS failed=${failedImages.length}`);
        } catch {
          addResumeDebugEvent("ws:inbound:UPLOADED_IMAGES_STATUS_parse_error");
        }
        return;
      }

      if (chunk_type === "TEXT") {
        try {
          const debugOutput = parseTextDebugOutput(chunk_data);

          if (debugOutput?.debugType === "stt_output") {
            useResumeDebugStore.getState().setLatestSttOutput(debugOutput.text);
            addResumeDebugEvent("ws:inbound:STT_OUTPUT", {
              source: debugOutput.source,
              text: debugOutput.text,
              confidence: debugOutput.confidence,
            });
            return;
          }

          if (debugOutput?.debugType === "tts_output") {
            useResumeDebugStore.getState().setLatestTtsOutput(debugOutput.text);
            window.dispatchEvent(
              new CustomEvent("vodemo:tts-output", { detail: debugOutput }),
            );
            addResumeDebugEvent("ws:inbound:TTS_OUTPUT", {
              source: debugOutput.source,
              text: debugOutput.text,
            });
            return;
          }

          if (debugOutput?.debugType === "tts_output") {
            useResumeDebugStore.getState().setLatestTtsOutput(debugOutput.text);
            window.dispatchEvent(
              new CustomEvent("vodemo:tts-output", { detail: debugOutput }),
            );
            addResumeDebugEvent("ws:inbound:TTS_OUTPUT", {
              source: debugOutput.source,
              text: debugOutput.text,
            });
            addLog("EVENT: TTS_OUTPUT");
            return;
          }
        } catch {
          addResumeDebugEvent("ws:inbound:TEXT_parse_error");
        }
      }

      if (
        shouldIgnoreExtraChunkAfterDone({
          doneChunkReceived: doneChunkRecievedRef.current,
          chunkType: chunk_type,
        })
      ) {
        addLog(`Ignoring extra chunk after DONE: ${chunk}`);
        return;
      }

      switch (chunk_type) {
        case "START": {
          const turnStart = parseTurnStart(chunk_data);
          emit(chunk_type, turnStart);
          addLog(
            `EVENT: ${chunk_type} submit_id=${turnStart.submitId ?? "none"}${turnStart.isMalformed ? " (malformed payload)" : ""}`,
          );
          useLoadStore.getState().setLoading(false);
          break;
        }
        case "DONE":
          emit(chunk_type);
          addLog(`EVENT: ${chunk_type}`);
          // Clear nudge flag once the tutor response to the inactivity nudge is
          // fully received, re-enabling canvas suppression for subsequent chunks.
          if (useAudioStore.getState().isNudgePending) {
            useAudioStore.getState().setIsNudgePending(false);
          }
          break;
        case "AUDIO":
          if (!firstAcceptedAudioChunkRef.current) {
            firstAcceptedAudioChunkRef.current = true;
            recordLessonStartupPerf(
              "ws_first_audio_chunk_received",
              {
                session_id: sessionId,
                lesson_id: useSlideStore.getState().currentLessonId,
                turn_id,
              },
              wsConnectStartedAtRef.current ?? undefined,
            );
          }
          emit("AUDIO_CHUNK", { chunk_data, turn_id });
          addLog(`EVENT: AUDIO_CHUNK`);
          break;
        case "CAPTIONS_UPDATE":
          emit("CAPTIONS_UPDATE", { chunk_data, turn_id });
          addLog(`EVENT: CAPTIONS_UPDATE`);
          break;
        case "DISCUSS_CONTEXT_FAILED": {
          const discussContextFailure = parseDiscussContextFailed(chunk_data);
          emit("DISCUSS_CONTEXT_FAILED", discussContextFailure);
          addLog(
            `EVENT: DISCUSS_CONTEXT_FAILED submit_id=${discussContextFailure.submitId ?? "none"} released=${discussContextFailure.released.join(",") || "none"}` +
              (discussContextFailure.ignoredSources.length
                ? ` ignored=${discussContextFailure.ignoredSources.join(",")}`
                : "") +
              (discussContextFailure.isMalformed ? " (malformed payload)" : ""),
          );
          break;
        }
      }
    };

    // Connect to unified WebSocket and store reconnect function
    const connectFn = connectWithRetry(
      wsRef,
      () => {
        const { addLog, emit } = latestRuntimeRef.current;
        useWebsocketStore.getState().setWsConnected(true);
        // Unlock mic send before WS_RECONNECTED so any DONE emitted from the
        // reconnect handler (e.g. mid-hold drop recovery) can reach the backend.
        micSendLockedRef.current = false;
        useTurnStore.getState().setTurnChangeFailed(false);
        const notepad = useNotepadStore.getState();
        if (selectIsNotepadOpen(notepad) && notepad.ingressType !== "student") {
          recordNotepadLifecycle("NOTEPAD_OPENED");
        } else {
          flushNotepadLifecycle();
        }
        addLog("[WS AT URL " + wsRef.current.url + " CONNECTED]");
        if (hasConnectedBeforeRef.current) {
          emit?.("WS_RECONNECTED");
        } else {
          hasConnectedBeforeRef.current = true;
        }
      },
      (event) => {
        const { addLog, emit } = latestRuntimeRef.current;
        useWebsocketStore.getState().setWsConnected(false);
        useAudioStore.getState().setIsNudgePending(false);
        // Lock mic send before WS_DISCONNECTED so any DONE emitted from the
        // disconnect handler (e.g. force-stop of mid-hold recording) is blocked
        // and doesn't land on the backend with a stale or missing turn context.
        micSendLockedRef.current = true;
        addLog(
          `[WS AT URL ${wsRef.current?.url} DISCONNECTED with code ${event.code}] t=${Date.now()}`,
        );
        emit?.("WS_DISCONNECTED");
      },
      handleMessage,
      (err) => {
        const { addLog } = latestRuntimeRef.current;
        addLog(`[WS Error] ${err.message || "Connection error"}`);
      },
    );

    // Store connect function for manual reconnection
    reconnectFnRef.current = connectFn;
    window.wsReconnect = connectFn; // Expose globally for debugging

    const closeActiveWebSocket = () => {
      const ws = activeSocket;
      debugLog("closing ws connection", ws);
      ws?.close(1000, "clean close");
      if (wsRef.current === ws) {
        wsRef.current = null;
        socketSessionIdRef.current = null;
      }
    };

    return () => {
      active = false;

      outboundMetricsRef.current?.logSummary();
      inboundMetricsRef.current?.logSummary();

      if (connectFn.cleanup) {
        connectFn.cleanup();
      }

      closeActiveWebSocket();
      useWebsocketStore.getState().setWsConnected(false);
      useAudioStore.getState().setIsNudgePending(false);
    };
  }, [sessionId, flushNotepadLifecycle, recordNotepadLifecycle]);
};
